import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import I10n from '../../features/i10n';
import DropdownMenu from '../dropdown_menu';
import InformationIcon from '../../atoms/information-icon';
import DynamicLabel from '../../atoms/dynamic-label';
import MsxIcons from '../../features/msx-icons';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ProductLink from '../product_link';
import ProductInformationCache from '../../persistence/description-cache';
import ToggleSwitch from '../../atoms/toggle-checkbox';
import Skeleton from '../skeleton';
import { PRIMARY_COLOR, SECONDARY_COLOR } from '../../color-constants';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  color: #626469;
  padding-top: 10px;
`;

const Row = styled.div`
  display: flex;
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
`;

const TitleRow = styled.div`
  display: flex;
  font-size: 24px;
  align-items: center;
  margin-bottom: 10px;
`;

const Title = styled.span`
`;

const InfoIconWrapper = styled.div`
  color: #BFBFBF;
  margin-left: 10px;
  cursor: pointer;
`;

const Hint = styled.div`
  color: #1F2729;
  padding: 10px;
  min-width: 200px;
  font-size: 14px;
`;

const GroupsScrollWrapper = styled.div`
  min-height: 300px;
  overflow : auto;
  padding: 0px 10px;
`;

const GroupsWrapper = styled.div`
  display: inline-flex;
  min-width: 100%;
  padding: 10px 5px;
  background-color: #F8F8F8;
`;

const PowerSupplyGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 10px;
  min-width: 232px;
  width: 232px;
  background-color: white;
  ${props => props.hasError && `
    border: 2px solid red;
  `}
`;

const PowerSupplyGroupHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${SECONDARY_COLOR};
  background-color: var(--se-primary, ${SECONDARY_COLOR});
  color: white !important;
  color: var(--se-primary-contrast, white) !important;
  padding: 10px;
  height: 30px;
  font-size: 16px;
`;

const PowerSupplyGroupContent = styled.div`
  padding: 10px;
  font-size: 14px;
  min-height: 250px;
  ${props => props.isDraggingOver && 'background-color: rgba(112, 112, 112, 0.11);'}
`;

const AddGroupButton = styled.div`
  display: flex;
  min-width: 232px;
  width: 232px;
  height: 46px;
  justify-content: center;
  align-items: center;
  border: 2px dashed ${PRIMARY_COLOR};
  border: 2px dashed var(--se-secondary, ${PRIMARY_COLOR});
  cursor: pointer;
  &[disabled] {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

const Plus = styled.span`
  color: ${PRIMARY_COLOR};
  color: var(--se-secondary, ${PRIMARY_COLOR});
  font-size: 24px;
  line-height: 42px;
  user-select: none;
`;

const IslandCard = styled.div`
  display: flex;
  margin-bottom: 10px;
  padding: 5px;
  box-shadow: 1px 1px 6px rgba(0, 0, 0, 0.161);
  ${props => props.canDrag && `@media all and (-ms-high-contrast: none) { cursor: move; }`}
`;

const IslandDragIconWrapper = styled.div`
  margin-right: 10px;
`;

const IslandLabel = styled.span`
  font-weight: bold;
  font-size: 14px;
`;

const Subtitle = styled.div`
  font-size: 14px;
  font-weight: bold;
  margin: 10px 0px;
`;

const PowerSupplyCardWrapper = styled.div`
  display: flex;
  padding-left: 5px;
`;

const PowerSupplyCard = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
  margin-right: 10px;
  padding: 10px 20px 10px 10px;
  border: 1px solid #ECEBEB;
  font-size: 12px;
  min-width: 200px;
  width: 200px;
`;

const PowerSupplyImage = styled.img`
  width: 40px;
  height: 40px;
  margin-right: 10px;
`;

const PowerSupplyDescription = styled.span`
  overflow: hidden;
  position: relative;

  max-height: 40px;
  margin-top: 10px;
  margin-right: -1em;
  padding-right: 1em;

  :after {
    content: '';
    position: absolute;
    right: 0;
    width: 1em;
    height: 1em;
    margin-top: 0.2em;
    background: white;
  }

  :before {
    content: '...';
    position: absolute;
    right: 0;
    bottom: 0;
  }

`;

const ToggleWrapper = styled(Row)`
  margin-top: 10px;
`;

const ToggleText = styled.span`
  margin-left: 10px;
`;

const LoadingWrapper = styled(Col)`
`;

const FindResults = styled.span`
  font-size: 12px;
  margin-bottom: 10px;
`;

const DeleteGroupIcon = styled.div`
  margin-left: 10px;
  cursor: pointer;
`;

class SegmentsGroupStep extends React.Component {

  static propTypes = {
    powerSupplyGroups: PropTypes.array,
    addPowerSupplyGroup: PropTypes.func,
    renamePowerSupplyGroup: PropTypes.func,
    removePowerSupplyGroup: PropTypes.func,
    moveIslandBetweenPowerSupplyGroups: PropTypes.func,
    powerSupplies: PropTypes.array,
    candidatePowerSupplies: PropTypes.array,
    togglePowerSupply: PropTypes.func,
    isLoading: PropTypes.bool,
  }

  get numberOfIslands() {
    let result = 0;
    this.props.powerSupplyGroups.forEach(group => {
      result += (group.islands || []).length
    });

    return result;
  }

  getPowerSupplyGroup = (id) => {
    return parseInt(id.replace('power-supply-group-', ''));
  }

  getIslandSlot = (id) => {
    return parseInt(id.replace('island-', ''));
  }

  onDragEnd = (result) => {

    const { source, destination } = result;

    // dropped outside the list
    if (!destination) {
      return;
    }

    if (source.droppableId !== destination.droppableId) {
      try {
        this.props.moveIslandBetweenPowerSupplyGroups(this.getPowerSupplyGroup(source.droppableId), this.getPowerSupplyGroup(destination.droppableId), this.getIslandSlot(result.draggableId));
      } catch (error) {
      }
    }
  }

  render = () => {
    const isAddGroupDisabled = this.props.isLoading || (this.props.powerSupplyGroups.length >= this.numberOfIslands);
    return (
      <Container>
        <TitleRow>
          <Title>{I10n.get('screen.power_supply_screen.group_step.title')}</Title>
          <DropdownMenu
            data-clickable
            toggleElement={<InfoIconWrapper slot="trigger"><InformationIcon size="small"></InformationIcon>
            </InfoIconWrapper>}
            containerElement={<Hint>{I10n.get('screen.power_supply_screen.group_step.hint')}</Hint>}
            forceLeft
          >
          </DropdownMenu>
        </TitleRow>
        <DragDropContext onDragEnd={this.onDragEnd}>
          <GroupsScrollWrapper>
            <GroupsWrapper>
              {this.props.powerSupplyGroups.map((group, idx) => {

                // True if no power supply is proposed for this group.
                const groupHasError = (!this.props.isLoading && (this.props.candidatePowerSupplies[idx] && this.props.candidatePowerSupplies[idx].id == null));

                return (
                  <PowerSupplyGroup key={idx} hasError={groupHasError}>
                    <PowerSupplyGroupHeader>
                      <DynamicLabel
                        value={group.name || I10n.getAndReplace(`screen.power_supply_screen.default_group_name`, idx + 1)}
                        setValue={(value) => {
                          if (value && value !== group.name)
                            this.props.renamePowerSupplyGroup(value, idx);
                        }}
                        enableEdit={!this.props.isLoading}
                        withCancel={true}
                        customInputProps={{ name: "groupName", maxLength: "20", color: 'white', size: "16" }}
                      >
                      </DynamicLabel>
                      {idx !== 0 && !this.props.isLoading && <DeleteGroupIcon><MsxIcons name="delete" size="nano" onClick={() => {
                        this.props.removePowerSupplyGroup(idx);
                      }}></MsxIcons></DeleteGroupIcon>}
                    </PowerSupplyGroupHeader>
                    <Droppable droppableId={`power-supply-group-${idx}`} isDropDisabled={this.props.isLoading}>
                      {(provided, snapshot) => (
                        <PowerSupplyGroupContent ref={provided.innerRef} isDraggingOver={snapshot.isDraggingOver}>
                          {group.islands.map((island, key) => {
                            let canDrag = (island.slot !== 0 && !this.props.isLoading);
                            return (
                              <Draggable key={island.slot}
                                draggableId={'island-' + island.slot} index={island.slot}
                                isDragDisabled={!canDrag}>
                                {(provided, snapshot) => (
                                  <IslandCard canDrag={canDrag}
                                    ref={provided.innerRef} {...provided.draggableProps}
                                    {...provided.dragHandleProps}>
                                    {island.slot !== 0 && <IslandDragIconWrapper><MsxIcons name="drag"></MsxIcons></IslandDragIconWrapper>}
                                    <IslandLabel>{island.slot === 0 ? I10n.get('screen.head_device_selector_screen.plc') : island.name}</IslandLabel>
                                  </IslandCard>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                        </PowerSupplyGroupContent>
                      )}
                    </Droppable>
                  </PowerSupplyGroup>
                );
              })}
              <AddGroupButton disabled={isAddGroupDisabled} onClick={() => {
                if (!isAddGroupDisabled) {
                  this.props.addPowerSupplyGroup();
                }
              }}><Plus>+</Plus></AddGroupButton>
            </GroupsWrapper>
            <Subtitle>{I10n.get('screen.power_supply_screen.suggested_power_supplies')}</Subtitle>
            {this.props.isLoading ? <LoadingWrapper>
              <FindResults>{I10n.get('screen.find_your_results_below')}</FindResults>
              <Skeleton nbCols={2} expand={false}></Skeleton>
            </LoadingWrapper> : <PowerSupplyCardWrapper>
              {this.props.powerSupplyGroups.map((group, idx) => {
                const candidate = this.props.candidatePowerSupplies[idx];
                return <PowerSupplyCard key={idx}>
                  {candidate != null && candidate.id != null ? <>
                    {
                      group.islands.length > 0 ? <>
                        <Row>
                          <PowerSupplyImage src={`https://gw-api.schneider-electric.com/sc/products/image/${candidate.id}?width=40&height=40`}></PowerSupplyImage>
                          <Col>
                            <ProductLink reference={candidate.id}></ProductLink>
                            <PowerSupplyDescription>{ProductInformationCache.getDescription(candidate.id)}</PowerSupplyDescription>
                          </Col>
                        </Row>
                        <ToggleWrapper>
                          <ToggleSwitch checked={candidate.isAdded} onToggle={(newValue) => {
                            this.props.togglePowerSupply(idx, newValue);
                          }}></ToggleSwitch>
                          <ToggleText>

                            {I10n.get(candidate.isAdded ? 'screen.power_supply_screen.added_to_bom' : 'screen.power_supply_screen.removed_from_bom')}
                          </ToggleText>

                        </ToggleWrapper>
                      </> : <ToggleText>{I10n.getAndReplace('screen.power_supply_screen.no_island', group.name || I10n.getAndReplace(`screen.power_supply_screen.default_group_name`, idx + 1))}</ToggleText>
                    }
                  </> :
                    <ToggleText>{I10n.getAndReplace('screen.power_supply_screen.no_power_supply_found', group.name || I10n.getAndReplace(`screen.power_supply_screen.default_group_name`, idx + 1))}</ToggleText>
                  }
                </PowerSupplyCard>
              })}
            </PowerSupplyCardWrapper>}
          </GroupsScrollWrapper>
        </DragDropContext>

      </Container >
    );
  }
}

export default SegmentsGroupStep;
